import { ChangeDetectionStrategy, Component, ElementRef, Input, ViewChild } from '@angular/core';
import { ManagementDocumentModel } from '@data-layer/models/management';
import { enumDocumentState, enumDocumentType } from '@data-layer/enums/documents';
import { take } from 'rxjs/operators';
import { DocumentsRepository } from '@data-layer/repositories';
import { enumLclTrackingStates } from '@enum/enumLclTrackingStates';

interface IFileItem {
  id: number;
  type: enumDocumentType;
  state: enumDocumentState;
  hasDoc: boolean;
  fileName?: string;
}

@Component({
  selector: 'lcl-results-files',
  templateUrl: './lcl-results-files.component.html',
  styleUrls: ['./lcl-results-files.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LclResultsFilesComponent {
  @Input() public docs: ManagementDocumentModel[] = [];
  @Input() public availableDocTypes: enumDocumentType[] = [];
  @Input() public shipmentState: enumLclTrackingStates;
  @Input() public isShipment: boolean;

  @ViewChild('link', { static: false }) public linkRef: ElementRef;

  public attachedDocuments: ManagementDocumentModel[] = [];

  public enumDocumentType: typeof enumDocumentType = enumDocumentType;

  constructor(private documentsRepository: DocumentsRepository) {}

  public downloadDocument(document: IFileItem): void {
    if (!document.hasDoc) {
      return;
    }

    this.documentsRepository
      .getDownloadLink(document.id)
      .pipe(take(1))
      .subscribe((linkData: { downloadUrl: string }) => {
        this.linkRef.nativeElement.href = linkData.downloadUrl;
        this.linkRef.nativeElement.click();
      });
  }

  public get resultFiles(): IFileItem[] {
    const result: IFileItem[] = [];
    const nonRequiredTypes: enumDocumentType[] = [
      enumDocumentType.FREIGHT_OTHER,
      enumDocumentType.FREIGHT_FREIGHT_DAMAGED_GOODS_PICS,
      enumDocumentType.FREIGHT_FREIGHT_REPACKED_GOODS_PICS,
    ];

    // #6923
    if (this.isShipment) {
      switch (this.shipmentState) {
        case enumLclTrackingStates.BOOKING_PLACED:
          nonRequiredTypes.push(
            ...[
              enumDocumentType.FREIGHT_HOUSE_BILL_OF_LANDING_DRAFT,
              enumDocumentType.FREIGHT_HOUSE_BILL_OF_LANDING,
              enumDocumentType.FREIGHT_ARRIVAL_NOTICE,
              enumDocumentType.FREIGHT_POD,
              enumDocumentType.FREIGHT_ISF_FORM,
              enumDocumentType.FREIGHT_RELEASE,
            ],
          );
          break;

        case enumLclTrackingStates.AT_ORIGIN_WAREHOUSE:
        case enumLclTrackingStates.IN_TRANSIT:
          nonRequiredTypes.push(...[enumDocumentType.FREIGHT_POD, enumDocumentType.FREIGHT_RELEASE]);
          break;
      }
    }

    this.availableDocTypes.forEach((typeItem: enumDocumentType) => {
      const existingDocs: ManagementDocumentModel[] = this.docs.filter(
        (docItem: ManagementDocumentModel) =>
          docItem.type === typeItem && ![enumDocumentState.CREATED, enumDocumentState.DELETED].includes(docItem.state),
      );

      if (!!existingDocs.length) {
        existingDocs.forEach((existingDocItem: ManagementDocumentModel) => {
          result.push({
            id: existingDocItem.id,
            type: existingDocItem.type,
            state: existingDocItem.state,
            hasDoc: true,
            fileName: existingDocItem.fileName,
          });
        });
      } else {
        if (!nonRequiredTypes.includes(typeItem)) {
          result.push({
            id: null,
            type: typeItem,
            state: enumDocumentState.CREATED,
            hasDoc: false,
          });
        }
      }
    });

    return result;
  }
}
